import React, { useContext, useEffect, useState } from 'react';
import { Box, Skeleton, SxProps, Typography, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Accordion } from '@europrocurement/flexy-components';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    DocTypeEnumType,
} from '@europrocurement/l2d-domain';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import {
    DocumentContext,
    useDocumentPreviewContext,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import useDossierDocuments, {
    DocumentExtension,
    DossierFichier,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocuments';
import useDossierDocumentsFiles from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocumentsFiles';
import {
    LAYOUT_DOCUMENTS_1_3,
    LAYOUT_DOCUMENTS_FULL,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import RequiredDocumentsList from '../../RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '../../RequiredDocumentUploader/RequiredDocumentUploader';

export type DossierDocumentsValidationListProps = {
    displayList: boolean;
    currentLayout?: string;
};

const DossierDocumentsValidationList: React.FunctionComponent<DossierDocumentsValidationListProps> =
    function (props) {
        const { displayList, currentLayout } = props;
        const { siblings } = useSelectedDossierContext();
        const [displayedDocType, setDisplayedDocType] =
            useState<DocTypeEnumType>(DOC_TYPE_REQUIRED);
        const [listItems, setListItems] = useState<DisplayedDocument[]>([]);
        const { getFilePreview } = useDossierDocumentsFiles();
        const { downloadFichier, isFileSupported } = useDossierDocuments();
        const {
            fichiers,
            documentDataLoaded,
            getInternalDocuments,
            getInternalAdditionalDocuments,
            getPrescriberDocuments,
            getPrescriberAdditionalDocuments,
            getFormalisteDocuments,
            getFormalisteAdditionalDocuments,
        } = useContext(DocumentContext);
        const dataLoaded = documentDataLoaded();
        const { setPreviewLoading, documentPreview, setDocumentPreview, setListDocuments } =
            useDocumentPreviewContext();
        const { palette } = useTheme();
        const [uploadingAdditionalDocument, setUploadingAdditionalDocument] =
            useState<boolean>(false);
        const { handleAddFile, handleRemoveFile, handleAdditionalUploads, handleMoveFile } =
            useDossierFile({
                listItems,
                setListItems,
                typeDoc: displayedDocType,
                setUploadingAdditionalDocument,
                source: 'BACK',
            });

        // Get displayed doc type list based on query params
        useEffect(() => {
            const urlParams = new URLSearchParams(document.location.search);
            const docType = urlParams.get('docType');
            if (docType) {
                setDisplayedDocType(+docType as DocTypeEnumType);
            }
        }, []);

        const handleSelect = async (
            documentLabel: string,
            fichierId: number,
            documentId: number,
        ) => {
            setPreviewLoading(true);

            try {
                const documentToShow = await getFilePreview(documentId, fichierId);

                if (!isFileSupported(documentToShow.fileType as DocumentExtension)) {
                    const fichier = fichiers.find(
                        (f: DossierFichier) => Number(f.id) === fichierId,
                    );

                    if (fichier) {
                        await downloadFichier(fichier);
                    }
                }

                setDocumentPreview(documentToShow);
                setPreviewLoading(false);
            } catch (err) {
                console.log(err);
                enqueueSnackbar(
                    `Une erreur est survenue lors de l'affichage du document ${documentLabel}`,
                    { variant: 'error' },
                );
            }
        };

        // Load list of documents by docType
        useEffect(() => {
            if (dataLoaded) {
                let arrayOfDatas: DisplayedDocument[] = [];

                if (displayedDocType === DOC_TYPE_REQUIRED) {
                    arrayOfDatas = [
                        ...getPrescriberDocuments(),
                        ...getPrescriberAdditionalDocuments(),
                    ];
                } else if (displayedDocType === DOC_TYPE_INTERNAL) {
                    arrayOfDatas = [...getInternalDocuments(), ...getInternalAdditionalDocuments()];
                } else if (displayedDocType === DOC_TYPE_DOWNLOADABLE) {
                    arrayOfDatas = [
                        ...getFormalisteDocuments(),
                        ...getFormalisteAdditionalDocuments(),
                    ];
                }

                // DO not display actions on this list
                arrayOfDatas = arrayOfDatas.map((data) => ({
                    ...data,
                    downloadable: false,
                    showable: !!data.fichierId,
                    uploadable: !data.fichierId,
                    smallUploadableBtn: true,
                }));

                setListItems(arrayOfDatas);
                setListDocuments(arrayOfDatas);

                // display the first document with file as preview
                const document = arrayOfDatas.find((doc) => doc.fichierId);
                if (document && !documentPreview) {
                    handleSelect(document.label, document.fichierId as number, document.id);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [displayedDocType, dataLoaded, fichiers]);

        let wrapperSx: SxProps = {
            display: displayList ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px 0',
            backgroundColor: palette.background.paper,
            borderRightColor: palette.grey[200],
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            padding: '8px 20px',
            overflowY: 'scroll',
            position: 'absolute',
            zIndex: 100,
            top: '0',
            left: '0',
            width: '40%',
            height: '100%',
        };

        if (currentLayout && currentLayout === LAYOUT_DOCUMENTS_FULL) {
            wrapperSx = { ...wrapperSx, width: '30%', position: 'sticky', overflowY: 'auto' };
        } else if (currentLayout && currentLayout === LAYOUT_DOCUMENTS_1_3) {
            wrapperSx = { ...wrapperSx, width: '45%', position: 'sticky', overflowY: 'auto' };
        }
        return (
            <Box sx={wrapperSx}>
                {!dataLoaded && (
                    <Box
                        sx={{
                            marginTop: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75em 0',
                        }}
                    >
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                    </Box>
                )}
                {dataLoaded && (
                    <>
                        {siblings.map((dossier) => {
                            const documents = listItems.filter(
                                (item) => item.dossierId === dossier.id,
                            );

                            if (!documents.length) {
                                return null;
                            }

                            const title = (
                                <Typography
                                    variant="l2dbodym"
                                    sx={{ color: palette.primary.dark }}
                                >
                                    {dossier.numero}
                                </Typography>
                            );

                            return (
                                <Accordion
                                    key={`accordion-${dossier.numero}`}
                                    title={title}
                                    expandLabel={null}
                                    expandColor={palette.primary}
                                    expanded
                                >
                                    <RequiredDocumentsList
                                        documents={documents}
                                        onUpload={handleAddFile}
                                        onDeletion={handleRemoveFile}
                                        onDownload={() => {}}
                                        onShow={handleSelect}
                                        onMove={handleMoveFile}
                                        onClick={() => {}}
                                    />
                                </Accordion>
                            );
                        })}
                        <RequiredDocumentUploader
                            uploading={uploadingAdditionalDocument}
                            onChange={handleAdditionalUploads}
                            showUploadBtn={false}
                        />
                    </>
                )}
            </Box>
        );
    };

export default DossierDocumentsValidationList;
